import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  return (
    <div className={`min-h-screen bg-gray-50 ${isAr ? 'rtl' : 'ltr'}`} data-testid="privacy-page">
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/" className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-900">
            {isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}
          </h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 pb-32">
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
          <p className="text-sm text-gray-500">{isAr ? 'آخر تحديث: مارس 2026' : 'Last Updated: March 2026'}</p>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '1. مقدمة' : '1. Introduction'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'تصف سياسة الخصوصية هذه كيف تقوم Findadz بجمع واستخدام وحماية معلوماتك الشخصية عند استخدام منصتنا. نحن ملتزمون بحماية خصوصيتك وفقًا لقوانين حماية البيانات في الإمارات العربية المتحدة.'
                : 'This Privacy Policy describes how Findadz collects, uses, and protects your personal information when you use our platform. We are committed to protecting your privacy in accordance with UAE data protection laws.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '2. المعلومات التي نجمعها' : '2. Information We Collect'}</h2>
            <h3 className="text-sm font-semibold text-gray-800">{isAr ? 'المعلومات المقدمة مباشرة:' : 'Information you provide directly:'}</h3>
            <ul className="text-sm text-gray-600 leading-relaxed list-disc list-inside space-y-2">
              <li>{isAr ? 'الاسم وعنوان البريد الإلكتروني ورقم الهاتف عند التسجيل' : 'Name, email address, and phone number during registration'}</li>
              <li>{isAr ? 'تفاصيل الإعلانات والصور التي تنشرها' : 'Listing details and images you post'}</li>
              <li>{isAr ? 'الرسائل المرسلة عبر نظام الدردشة' : 'Messages sent through our chat system'}</li>
              <li>{isAr ? 'معلومات الملف الشخصي والتفضيلات' : 'Profile information and preferences'}</li>
            </ul>
            <h3 className="text-sm font-semibold text-gray-800 mt-4">{isAr ? 'المعلومات المجمعة تلقائيًا:' : 'Information collected automatically:'}</h3>
            <ul className="text-sm text-gray-600 leading-relaxed list-disc list-inside space-y-2">
              <li>{isAr ? 'عنوان IP ونوع المتصفح ومعلومات الجهاز' : 'IP address, browser type, and device information'}</li>
              <li>{isAr ? 'بيانات الاستخدام وسجل التصفح على المنصة' : 'Usage data and browsing history on the platform'}</li>
              <li>{isAr ? 'بيانات الموقع الجغرافي (بموافقتك)' : 'Location data (with your consent)'}</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '3. كيف نستخدم معلوماتك' : '3. How We Use Your Information'}</h2>
            <ul className="text-sm text-gray-600 leading-relaxed list-disc list-inside space-y-2">
              <li>{isAr ? 'تشغيل وصيانة المنصة' : 'To operate and maintain the platform'}</li>
              <li>{isAr ? 'تسهيل التواصل بين المشترين والبائعين' : 'To facilitate communication between buyers and sellers'}</li>
              <li>{isAr ? 'تحسين خدماتنا وتجربة المستخدم' : 'To improve our services and user experience'}</li>
              <li>{isAr ? 'إرسال إشعارات حول حسابك وإعلاناتك' : 'To send notifications about your account and listings'}</li>
              <li>{isAr ? 'كشف ومنع الاحتيال والأنشطة غير المصرح بها' : 'To detect and prevent fraud and unauthorized activities'}</li>
              <li>{isAr ? 'الامتثال للمتطلبات القانونية والتنظيمية' : 'To comply with legal and regulatory requirements'}</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '4. مشاركة المعلومات' : '4. Information Sharing'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'لا نبيع معلوماتك الشخصية لأطراف ثالثة. قد نشارك معلوماتك في الحالات التالية:'
                : 'We do not sell your personal information to third parties. We may share your information in the following cases:'}
            </p>
            <ul className="text-sm text-gray-600 leading-relaxed list-disc list-inside space-y-2">
              <li>{isAr ? 'مع مستخدمين آخرين عند التواصل بشأن إعلان' : 'With other users when communicating about a listing'}</li>
              <li>{isAr ? 'مع مزودي الخدمات الذين يساعدوننا في تشغيل المنصة' : 'With service providers who help us operate the platform'}</li>
              <li>{isAr ? 'عندما يقتضي القانون أو بأمر من المحكمة' : 'When required by law or court order'}</li>
              <li>{isAr ? 'لحماية حقوقنا وسلامة مستخدمينا' : 'To protect our rights and the safety of our users'}</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '5. أمن البيانات' : '5. Data Security'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'نتخذ تدابير أمنية معقولة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو الإفصاح أو التعديل أو الإتلاف. تشمل هذه التدابير التشفير وضوابط الوصول والمراجعات الأمنية المنتظمة.'
                : 'We implement reasonable security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include encryption, access controls, and regular security audits.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '6. ملفات تعريف الارتباط' : '6. Cookies'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'نستخدم ملفات تعريف الارتباط والتقنيات المماثلة لتحسين تجربتك على المنصة. يمكنك التحكم في إعدادات ملفات تعريف الارتباط من خلال متصفحك.'
                : 'We use cookies and similar technologies to enhance your experience on the platform. You can control cookie settings through your browser preferences.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '7. حقوقك' : '7. Your Rights'}</h2>
            <ul className="text-sm text-gray-600 leading-relaxed list-disc list-inside space-y-2">
              <li>{isAr ? 'الوصول إلى معلوماتك الشخصية وتصحيحها' : 'Access and correct your personal information'}</li>
              <li>{isAr ? 'طلب حذف حسابك وبياناتك' : 'Request deletion of your account and data'}</li>
              <li>{isAr ? 'إلغاء الاشتراك في الاتصالات التسويقية' : 'Opt-out of marketing communications'}</li>
              <li>{isAr ? 'طلب نسخة من بياناتك الشخصية' : 'Request a copy of your personal data'}</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '8. خصوصية الأطفال' : '8. Children\'s Privacy'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'خدمتنا غير موجهة للأطفال دون سن 18 عامًا. نحن لا نجمع عن قصد معلومات شخصية من الأطفال.'
                : 'Our service is not directed to children under 18 years of age. We do not knowingly collect personal information from children.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '9. التغييرات على هذه السياسة' : '9. Changes to This Policy'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سنخطرك بأي تغييرات جوهرية عن طريق نشر السياسة الجديدة على المنصة.'
                : 'We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on the platform.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '10. الاتصال بنا' : '10. Contact Us'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'إذا كانت لديك أي أسئلة حول سياسة الخصوصية هذه، يرجى الاتصال بنا على privacy@findadz.com'
                : 'If you have any questions about this Privacy Policy, please contact us at privacy@findadz.com'}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
